import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useProfitVisibility } from './useProfitVisibility';

/**
 * 路由级权限守卫（2026-09-08，PRD 4.3 L3）
 *
 * 集中维护"需利润可见性"的路由黑名单：三级代理（profit_visible=false）
 * 直接访问受保护路由（如 /m/commission）时重定向到 /m/home 并弹提示。
 *
 * 使用方式（App.tsx）：
 *   <Route path="commission" element={<ProfitRouteGuard><MobileCommissionPage /></ProfitRouteGuard>} />
 */

/** 利润受限路由清单（集中维护，新增受限路由时在此登记） */
export const PROFIT_RESTRICTED_ROUTES: string[] = ['/m/commission'];

/**
 * 路由守卫钩子：判断当前用户是否可访问指定路由
 * @param routeKey 路由黑名单中的路径（如 '/m/commission'）
 * @returns true = 允许访问；false = 拦截（应重定向）
 */
export function useRouteGuard(routeKey: string): boolean {
  const profitVisible = useProfitVisibility();
  const restricted = PROFIT_RESTRICTED_ROUTES.includes(routeKey);
  // 非受限路由一律放行；受限路由按利润可见性判定
  if (!restricted) return true;
  return profitVisible;
}

/**
 * 利润路由守卫组件：
 * - profit_visible=false（三级代理）→ Navigate 到 /m/home + alert 提示（仅提示一次）
 * - 其余 → 正常渲染子组件
 */
export function ProfitRouteGuard({ children }: { children: ReactNode }) {
  const allowed = useRouteGuard('/m/commission');
  const alertedRef = useRef(false);

  useEffect(() => {
    if (!allowed && !alertedRef.current) {
      alertedRef.current = true;
      alert('您的角色不提供分账明细查看');
    }
  }, [allowed]);

  if (!allowed) {
    return <Navigate to="/m/home" replace />;
  }
  return <>{children}</>;
}
